import { useState } from "react";
import { Helmet } from 'react-helmet-async';
import { Link, useLocation } from "react-router-dom";
import { useQuery } from '@tanstack/react-query';
import { getCanonicalUrl, getDefaultOgImage } from '@/lib/seoUtils';
import { Header } from "@/components/layout/Header";
import { Footer } from "@/components/layout/Footer";
import { Button } from "@/components/ui/button";
import { ArrowRight, Calendar, User, Search, AlertCircle } from "lucide-react";
import { blogPosts, blogCategories as categories } from "@/lib/blogPosts";
import { fetchBlogPosts, type BlogPostSummaryResponse } from "@/services/seoService";
import { isReleaseFlagOn } from '@/config/featureFlags';

/* ── Static mode (flag OFF) ── */
function StaticBlogContent() {
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("All");

  const filteredPosts = blogPosts.filter((post) => {
    const matchesSearch = post.title.toLowerCase().includes(searchQuery.toLowerCase())
      || post.excerpt.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesCategory = selectedCategory === "All" || post.category === selectedCategory;
    return matchesSearch && matchesCategory;
  });

  const scrollToCategory = (category: string) => {
    setSelectedCategory(category);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <>
      {/* Hero Section */}
      <section className="relative pt-28 pb-16 sm:pb-24 lg:pt-44 bg-gradient-to-b from-indigo-50 to-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Learning Hub &amp; Insights
            </h1>
            <p className="text-lg text-[#5A5E80] mb-8">
              Discover tips, strategies, and stories from our community of learners and teachers.
            </p>
            <div className="relative max-w-md mx-auto">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
              <input
                type="text"
                placeholder="Search articles..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-3 rounded-lg border border-[#DCDDEE] focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Categories Filter */}
      <section className="border-b border-[#DCDDEE] sticky top-[73px] lg:top-[148px] bg-white z-10">
        <div className="container mx-auto px-4">
          <div className="flex overflow-x-auto gap-2 py-4">
            {categories.map((category) => (
              <button
                key={category}
                onClick={() => scrollToCategory(category)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-150 cursor-pointer",
                  selectedCategory === category
                    ? "bg-indigo-600 text-white"
                    : "bg-[#EEEFF9] text-[#262A7A] hover:bg-gray-200"
                )}
              >
                {category}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Blog Posts Grid */}
      <section className="flex-1 py-12 sm:py-16">
        <div className="container mx-auto px-4">
          {filteredPosts.length === 0 ? (
            <div className="text-center py-12">
              <p className="text-lg text-[#5A5E80] mb-4">No articles found matching your search.</p>
              <Button
                onClick={() => { setSearchQuery(""); setSelectedCategory("All"); }}
                variant="outline"
              >
                Clear Filters
              </Button>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.id}`} className="block">
                  <article className="bg-white rounded-lg border border-[#DCDDEE] overflow-hidden hover:shadow-lg transition-shadow duration-200 h-full cursor-pointer">
                    <div className="h-48 bg-[#EEEFF9] overflow-hidden">
                      <img
                        src={post.image}
                        alt={post.title}
                        className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                      />
                    </div>
                    <div className="p-6 flex flex-col h-full">
                      <div className="mb-3">
                        <span className="inline-block px-2.5 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                          {post.category}
                        </span>
                      </div>
                      <h3 className="text-lg font-semibold text-[#1D2045] mb-2 line-clamp-2">{post.title}</h3>
                      <p className="text-[#5A5E80] text-sm mb-4 line-clamp-2 flex-1">{post.excerpt}</p>
                      <div className="space-y-3 pt-4 border-t border-gray-100">
                        <div className="flex items-center gap-4 text-xs text-[#5A5E80]">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {post.date}
                          </div>
                          <div>{post.readTime}</div>
                        </div>
                        <div className="flex items-center justify-between pt-2">
                          <div className="flex items-center gap-2 text-sm">
                            <User className="w-4 h-4 text-gray-400" />
                            <span className="text-[#5A5E80]">{post.author}</span>
                          </div>
                          <ArrowRight className="w-4 h-4 text-indigo-600" />
                        </div>
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>
    </>
  );
}

/* ── API-driven mode (flag ON) ── */
function ApiBlogContent() {
  const [selectedTag, setSelectedTag] = useState<string | undefined>(undefined);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [allPosts, setAllPosts] = useState<BlogPostSummaryResponse[]>([]);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['blog-posts', selectedTag, cursor],
    queryFn: async () => {
      const result = await fetchBlogPosts({ tag: selectedTag, cursor, size: 9 });
      setAllPosts((prev) => cursor ? [...prev, ...result.posts] : result.posts);
      return result;
    },
    staleTime: 60_000,
  });

  const allTags = Array.from(new Set(allPosts.flatMap((p) => p.tags)));

  const handleTagClick = (tag: string | undefined) => {
    setSelectedTag(tag);
    setCursor(undefined);
    setAllPosts([]);
  };

  return (
    <>
      {/* Hero */}
      <section className="relative pt-28 pb-16 sm:pb-24 lg:pt-44 bg-gradient-to-b from-indigo-50 to-white overflow-hidden">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h1 className="text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Learning Hub &amp; Insights
            </h1>
            <p className="text-lg text-[#5A5E80]">
              Discover tips, strategies, and stories from our community of learners and teachers.
            </p>
          </div>
        </div>
      </section>

      {/* Tag filter chips */}
      {allTags.length > 0 && (
        <section className="border-b border-[#DCDDEE] sticky top-[73px] lg:top-[148px] bg-white z-10">
          <div className="container mx-auto px-4">
            <div className="flex overflow-x-auto gap-2 py-4">
              <button
                onClick={() => handleTagClick(undefined)}
                className={cn(
                  "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-150 cursor-pointer",
                  selectedTag === undefined
                    ? "bg-indigo-600 text-white"
                    : "bg-[#EEEFF9] text-[#262A7A] hover:bg-gray-200"
                )}
              >
                All
              </button>
              {allTags.map((tag) => (
                <button
                  key={tag}
                  onClick={() => handleTagClick(tag)}
                  className={cn(
                    "px-4 py-2 rounded-full text-sm font-medium whitespace-nowrap transition-colors duration-150 cursor-pointer",
                    selectedTag === tag
                      ? "bg-indigo-600 text-white"
                      : "bg-[#EEEFF9] text-[#262A7A] hover:bg-gray-200"
                  )}
                >
                  {tag}
                </button>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Posts */}
      <section className="flex-1 py-12 sm:py-16">
        <div className="container mx-auto px-4">
          {isError && (
            <div className="flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700 mb-8">
              <AlertCircle className="w-5 h-5 flex-shrink-0" />
              <p>Failed to load blog posts. Please try again later.</p>
            </div>
          )}

          {isLoading && allPosts.length === 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-lg border border-[#DCDDEE] overflow-hidden animate-pulse">
                  <div className="h-48 bg-gray-200" />
                  <div className="p-6 space-y-3">
                    <div className="h-4 bg-gray-200 rounded w-1/3" />
                    <div className="h-5 bg-gray-200 rounded w-3/4" />
                    <div className="h-4 bg-gray-200 rounded w-full" />
                    <div className="h-4 bg-gray-200 rounded w-2/3" />
                  </div>
                </div>
              ))}
            </div>
          )}

          {!isLoading && !isError && allPosts.length === 0 && (
            <div className="text-center py-12">
              <p className="text-lg text-[#5A5E80]">No posts yet. Check back soon!</p>
            </div>
          )}

          {allPosts.length > 0 && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {allPosts.map((post) => (
                <Link key={post.id} to={`/blog/${post.slug}`} className="block cursor-pointer">
                  <article className="bg-white rounded-lg border border-[#DCDDEE] overflow-hidden hover:shadow-lg transition-shadow duration-200 h-full">
                    <div className="p-6 flex flex-col h-full">
                      {post.tags.length > 0 && (
                        <div className="mb-3 flex flex-wrap gap-1">
                          {post.tags.map((tag) => (
                            <span key={tag} className="inline-block px-2.5 py-1 text-xs font-medium bg-indigo-100 text-indigo-700 rounded-full">
                              {tag}
                            </span>
                          ))}
                        </div>
                      )}
                      <h3 className="text-lg font-semibold text-[#1D2045] mb-2 line-clamp-2">{post.title}</h3>
                      <div className="flex items-center gap-4 text-xs text-[#5A5E80] mt-auto pt-4 border-t border-gray-100">
                        {post.authorName && (
                          <div className="flex items-center gap-1">
                            <User className="w-4 h-4" />
                            {post.authorName}
                          </div>
                        )}
                        {post.publishedAt && (
                          <div className="flex items-center gap-1">
                            <Calendar className="w-4 h-4" />
                            {new Date(post.publishedAt).toLocaleDateString('en-IN', { year: 'numeric', month: 'short', day: 'numeric' })}
                          </div>
                        )}
                        <ArrowRight className="w-4 h-4 text-indigo-600 ml-auto" />
                      </div>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          )}

          {data?.hasMore && (
            <div className="text-center mt-10">
              <Button
                onClick={() => setCursor(data.nextCursor)}
                variant="outline"
                className="cursor-pointer"
                disabled={isLoading}
              >
                {isLoading ? 'Loading...' : 'Load more'}
              </Button>
            </div>
          )}
        </div>
      </section>
    </>
  );
}

/* ── Page shell ── */
export default function BlogPage() {
  const { pathname } = useLocation();
  const flagOn = isReleaseFlagOn('SEO_PANEL');

  const title = 'Blog | BookMyCoaching';
  const description = 'Tips, guides, and insights for students, parents, and tutors from the BookMyCoaching team.';
  const canonicalUrl = getCanonicalUrl(pathname);
  const ogImage = getDefaultOgImage();

  return (
    <div className="min-h-screen bg-white flex flex-col">
      <Helmet>
        <title>{title}</title>
        <meta name="description" content={description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={title} />
        <meta property="og:description" content={description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:image" content={ogImage} />
      </Helmet>
      <Header />

      {flagOn ? <ApiBlogContent /> : <StaticBlogContent />}

      {/* CTA Section */}
      <section className="bg-gradient-to-r from-indigo-600 to-indigo-700 text-white py-12 sm:py-16">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl sm:text-4xl font-bold mb-4">Ready to Find Your Perfect Teacher?</h2>
          <p className="text-lg text-indigo-100 mb-8 max-w-2xl mx-auto">
            Start your learning journey today and connect with verified teachers who match your goals.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/search">
              <Button size="lg" variant="secondary" className="cursor-pointer">
                Explore Teachers
              </Button>
            </Link>
            <Link to="/teacher-profile-setup">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 cursor-pointer">
                Become a Teacher
              </Button>
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}

function cn(...classes: (string | undefined | null | false)[]) {
  return classes.filter(Boolean).join(" ");
}
